import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';

import config from 'constants/apiConfig';
import { useListExternalLayersQuery } from 'hooks/externalLayer/useListExternalLayersQuery';
import {
  keys,
  mapResourceSetToEditPayload,
  resourceSetToApiBody,
  useResourceSets,
  type ResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';

import {
  isQueryableFeatureService,
  parseArcgisLocationLink,
  queryArcgisFeaturePoints,
} from './arcgisFeatureLocation';

const refreshing = new Set<string>();

function resourceRows(data: unknown): ResourceSet[] {
  if (Array.isArray(data)) return data;
  if (
    data &&
    typeof data === 'object' &&
    Array.isArray((data as { rows?: unknown }).rows)
  ) {
    return (data as { rows: ResourceSet[] }).rows;
  }
  return [];
}

function storedPoint(resource: ResourceSet): [number, number] | undefined {
  const candidates = [resource.geolocation, resource.geometry];
  for (const value of candidates) {
    if (!value || typeof value !== 'object') continue;
    const coordinates = (value as { coordinates?: unknown }).coordinates;
    if (
      Array.isArray(coordinates) &&
      typeof coordinates[0] === 'number' &&
      typeof coordinates[1] === 'number'
    ) {
      return [coordinates[0], coordinates[1]];
    }
  }
  return undefined;
}

function pointsDiffer(
  current: [number, number] | undefined,
  next: [number, number]
) {
  if (!current) return true;
  return (
    Math.abs(current[0] - next[0]) > 1e-5 ||
    Math.abs(current[1] - next[1]) > 1e-5
  );
}

/**
 * While the map is open, rewrite a linked asset point when its ArcGIS feature
 * has moved. The ArcGIS session lives in this browser, so the check does too.
 */
export function useRefreshArcgisLinkedLocations() {
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const resourcesQuery = useResourceSets();
  const layersQuery = useListExternalLayersQuery(
    {
      filter: { groupGuid: groupGuid ?? '' },
      perPage: 200,
    },
    { enabled: Boolean(groupGuid) }
  );

  const resources = resourceRows(resourcesQuery.data);
  const layers = layersQuery.data?.data ?? [];
  const layerSignature = layers
    .map((layer) => `${layer.id}:${layer.serviceUrl ?? ''}`)
    .join('|');
  const linkSignature = resources
    .map((resource) => {
      const link = parseArcgisLocationLink(resource.arcgis_location_link);
      const point = storedPoint(resource);
      return link
        ? `${resource.resource_set_id}:${link.external_layer_id}:${link.object_id}:${point?.[0] ?? ''}:${point?.[1] ?? ''}`
        : '';
    })
    .filter(Boolean)
    .join('|');

  useEffect(() => {
    if (!groupGuid || !userGuid || !linkSignature) return;
    const layersById = new Map(
      layers
        .filter((layer) => isQueryableFeatureService(layer.serviceUrl))
        .map((layer) => [layer.id, layer.serviceUrl as string])
    );
    const pending = resources.flatMap((resource) => {
      const link = parseArcgisLocationLink(resource.arcgis_location_link);
      const serviceUrl = link
        ? layersById.get(link.external_layer_id)
        : undefined;
      if (!link || !serviceUrl || refreshing.has(resource.resource_set_id)) {
        return [];
      }
      return [{ resource, link, serviceUrl }];
    });
    if (pending.length === 0) return;

    const byUrl = new Map<string, typeof pending>();
    for (const item of pending) {
      const group = byUrl.get(item.serviceUrl) ?? [];
      group.push(item);
      byUrl.set(item.serviceUrl, group);
    }

    let cancelled = false;
    for (const item of pending) refreshing.add(item.resource.resource_set_id);

    void (async () => {
      let changed = false;
      try {
        for (const [serviceUrl, items] of byUrl) {
          const points = await queryArcgisFeaturePoints(
            serviceUrl,
            items.map((item) => item.link.object_id)
          );
          if (cancelled) return;
          for (const item of items) {
            const next = points.get(item.link.object_id);
            if (!next || !pointsDiffer(storedPoint(item.resource), next)) {
              continue;
            }
            const payload = mapResourceSetToEditPayload(
              item.resource.resource_set_id,
              item.resource
            );
            await axios.put(
              `${config.apiGateway.ics_resource_sets}/${item.resource.resource_set_id}`,
              {
                ...resourceSetToApiBody({
                  ...payload,
                  geometry: { type: 'Point', coordinates: next },
                  arcgisLocationLink: item.link,
                }),
                user_guid: userGuid,
                group_guid: groupGuid,
              }
            );
            changed = true;
          }
        }
      } catch (error) {
        console.error(
          '[arcgis-location] failed to refresh linked assets',
          error
        );
      } finally {
        for (const item of pending) {
          refreshing.delete(item.resource.resource_set_id);
        }
        if (!cancelled && changed) {
          await queryClient.invalidateQueries({ queryKey: keys.all });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [groupGuid, userGuid, linkSignature, layerSignature, queryClient]);
}
